// src/services/stripe-service.js - UNIFIED VERSION with dual Firebase support + unified credit config
import { logger } from "../utils/logger";
import { BACKEND_CREDIT_CONFIG, FAN_TYPE_MAPPINGS } from "../utils/unified-credit-config";

class StripeService {
  constructor() {
    this.stripe = null;

    // Firebase (compat)
    this.functions = null;

    // Firebase (modular v10)
    this.modernFunctions = null;

    this.initialized = false;
    this.useModernFirebase = false;
  }

  // -----------------------------
  // Initialization
  // -----------------------------
  async initialize() {
    if (this.initialized) return;

    try {
      await this.initializeStripe();
      await this.initializeFirebase();
      this.initialized = true;
      logger.success("✅ StripeService fully initialized");
    } catch (error) {
      console.error("❌ StripeService initialization error:", error);
      throw error;
    }
  }

  async initializeStripe() {
    if (typeof window === "undefined" || !window.Stripe) {
      throw new Error("Stripe SDK not loaded");
    }

    const stripeKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
    if (!stripeKey) {
      throw new Error("REACT_APP_STRIPE_PUBLISHABLE_KEY not found");
    }

    this.stripe = window.Stripe(stripeKey);

    if (stripeKey.startsWith("pk_live_")) {
      console.log("🔴 LIVE MODE: Real payments will be processed");
    } else {
      console.log("🟡 TEST MODE: Using test payments");
    }

    logger.success("✅ Stripe initialized");
  }

  async initializeFirebase() {
    try {
      // Prefer modern Firebase v10 if app has it wired
      if (typeof window !== "undefined" && window.FIREBASE_READY) {
        try {
          const { httpsCallable } = await import("firebase/functions");
          const { functions } = await import("../config/firebase-config.js");
          if (functions && httpsCallable) {
            this.modernFunctions = { functions, httpsCallable };
            this.useModernFirebase = true;
            logger.firebase("✅ Modern Firebase Functions (v10) initialized");
            return;
          }
        } catch {
          logger.warn("⚠️ Modern Firebase not available, trying compat");
        }
      }

      // Fallback to compat (v8-style)
      await this.waitForCompatFirebase();

      if (window.firebase?.functions) {
        this.functions = window.firebase.functions();
        if (!this.functions?.httpsCallable) {
          throw new Error("Firebase Functions not properly initialized");
        }
        this.useModernFirebase = false;
        logger.firebase("✅ Compat Firebase Functions initialized");
      } else {
        throw new Error("Firebase Functions not available");
      }
    } catch (error) {
      console.error("❌ Firebase initialization failed:", error);
      throw new Error("Firebase Functions initialization failed");
    }
  }

  async waitForCompatFirebase() {
    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => reject(new Error("Firebase compat initialization timeout")), 10000);
      const check = () => {
        if (window.firebase?.auth && window.firebase?.functions) {
          clearTimeout(timeout);
          resolve();
        } else {
          setTimeout(check, 100);
        }
      };
      check();
    });
  }

  // -----------------------------
  // Firebase callable wrapper
  // -----------------------------
  async callFunction(functionName, data) {
    try {
      if (this.useModernFirebase && this.modernFunctions) {
        const { functions, httpsCallable } = this.modernFunctions;
        const func = httpsCallable(functions, functionName);
        const result = await func(data);
        return result.data;
      }
      if (this.functions) {
        const func = this.functions.httpsCallable(functionName);
        const result = await func(data);
        return result.data;
      }
      throw new Error("No Firebase Functions available");
    } catch (error) {
      console.error(`❌ Error calling function ${functionName}:`, error);
      throw error;
    }
  }

  // -----------------------------
  // Auth helpers (compat)
  // -----------------------------
  getCurrentUser() {
    if (!window.firebase?.auth) throw new Error("Firebase auth not initialized");
    const user = window.firebase.auth().currentUser;
    if (!user) throw new Error("User not authenticated");
    if (user.isAnonymous) throw new Error("Anonymous users cannot make purchases");
    if (!user.email) throw new Error("User email required for payments");
    return user;
  }

  async ensureAuthenticated() {
    return new Promise((resolve, reject) => {
      try {
        const u = this.getCurrentUser();
        if (u) {
          logger.success("✅ User already authenticated:", u.uid);
          return resolve(u);
        }
      } catch {
        // fall through to onAuthStateChanged
      }

      const timeout = setTimeout(() => reject(new Error("Authentication timeout")), 5000);
      const unsubscribe = window.firebase.auth().onAuthStateChanged((user) => {
        clearTimeout(timeout);
        unsubscribe();
        if (!user) return reject(new Error("User not authenticated"));
        if (user.isAnonymous) return reject(new Error("Anonymous users cannot make purchases"));
        if (!user.email) return reject(new Error("User email required for payments"));
        logger.success("✅ User authenticated:", user.uid);
        resolve(user);
      });
    });
  }

  // -----------------------------
  // Main: create credit payment
  // -----------------------------
  async createCreditPayment(data) {
    try {
      logger.firebase("🔥 Creating credit payment:", data);

      await this.initialize();
      await this.ensureAuthenticated();

      // Normalize fanType to backend expectation ('diehard' → 'expert')
      let normalized = data?.fanType ? FAN_TYPE_MAPPINGS.toBackend(data.fanType) : undefined;
      const payload = { ...data, fanType: normalized };

      const response = await this.callFunction("createCreditPayment", payload);

      logger.success("✅ Credit payment session created:", response);
      return {
        sessionId: response.sessionId,
        url: response.url,
        feature: response.feature,
        mode: response.mode || "UNKNOWN",
      };
    } catch (error) {
      console.error("❌ Error creating credit payment:", error);
      // Nice messages for common Function codes
      switch (error.code) {
        case "functions/unauthenticated":
          throw new Error("Please sign in to continue with your purchase");
        case "functions/permission-denied":
          throw new Error("You don't have permission to make this purchase");
        case "functions/invalid-argument":
          throw new Error("Invalid purchase information. Please try again.");
        case "functions/internal":
          throw new Error("Payment service temporarily unavailable. Please try again.");
        default:
          throw new Error(error.message || "Failed to create payment session");
      }
    }
  }

  // Legacy aliases
  async createCreditCheckoutSession(priceId, fanType) {
    return this.createCreditPayment({
      priceId,
      fanType,
      returnUrl: window.location.origin + window.location.pathname,
    });
  }

  async createCheckoutSession(priceId, options = {}) {
    logger.credits("💳 Creating checkout session (legacy):", { priceId, options });
    return this.createCreditPayment({
      priceId,
      fanType: options.fanType || "dedicated",
      returnUrl: window.location.origin + window.location.pathname,
      ...options,
    });
  }

  // -----------------------------
  // Verify + Balance
  // -----------------------------
  async verifyCreditPurchase(sessionId) {
    try {
      await this.initialize();
      await this.ensureAuthenticated();
      const response = await this.callFunction("verifyCheckoutSession", { sessionId });
      logger.success("✅ Credit purchase verified:", response);
      return response;
    } catch (error) {
      console.error("❌ Error verifying credit purchase:", error);
      throw new Error(error.message || "Failed to verify purchase");
    }
  }

  async getCreditBalance() {
    try {
      await this.initialize();
      await this.ensureAuthenticated();
      const response = await this.callFunction("getCreditBalance", {});
      logger.success("💰 Credit balance retrieved:", response);
      return response;
    } catch (error) {
      console.error("❌ Error getting credit balance:", error);
      throw new Error(error.message || "Failed to get credit balance");
    }
  }

  // -----------------------------
  // Validation helpers
  // -----------------------------
  // Use unified config list of priceIds (safer than hardcoding)
  isValidPriceId(priceId) {
    return Object.prototype.hasOwnProperty.call(BACKEND_CREDIT_CONFIG, priceId);
  }

  isLiveMode() {
    return process.env.NODE_ENV === 'production' || process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY?.startsWith("pk_live_");
  }

  async purchaseCredits(priceId, fanType) {
    try {
      console.log(`💰 Starting credit purchase: ${priceId} (${fanType})`);

      if (!this.isValidPriceId(priceId)) {
        console.error("❌ Invalid price ID:", priceId);
        throw new Error("Invalid price ID - this price is not available");
      }

      if (this.isLiveMode()) {
        console.log("🔴 LIVE MODE: Processing real payment");
      } else {
        console.log("🟡 TEST MODE: Using test payment");
      }

      // Accept 'diehard' or 'expert' (normalize for backend)
      const normalized = FAN_TYPE_MAPPINGS.toBackend(fanType || "dedicated");
      if (!["dedicated", "expert"].includes(normalized)) {
        throw new Error("Invalid fan type");
      }

      const checkoutData = await this.createCreditCheckoutSession(priceId, normalized);
      logger.success("✅ Credit purchase checkout session ready");
      return checkoutData;
    } catch (error) {
      console.error("❌ Credit purchase error:", error);
      throw new Error(`Credit purchase failed: ${error.message}`);
    }
  }

  // -----------------------------
  // Billing Portal
  // -----------------------------
  async createPortalSession(customerId) {
    try {
      await this.initialize();
      await this.ensureAuthenticated();

      const response = await this.callFunction("createPortalSession", {
        customerId,
        returnUrl: window.location.origin,
      });

      if (response?.url) {
        window.location.href = response.url;
      } else {
        throw new Error("No portal URL received");
      }
    } catch (error) {
      console.error("❌ Error creating portal session:", error);
      throw new Error(error.message || "Failed to open billing portal");
    }
  }

  // -----------------------------
  // Payment Intents (future use)
  // -----------------------------
  async createPaymentIntent(amount, currency = "usd", metadata = {}) {
    try {
      await this.initialize();
      const response = await this.callFunction("createPaymentIntent", { amount, currency, metadata });
      return response;
    } catch (error) {
      console.error("❌ Payment intent creation failed:", error);
      throw error;
    }
  }

  async confirmPayment(clientSecret, paymentMethod) {
    try {
      if (!this.stripe) throw new Error("Stripe not initialized");
      const result = await this.stripe.confirmPayment({
        clientSecret,
        payment_method: paymentMethod,
      });
      return result;
    } catch (error) {
      console.error("❌ Payment confirmation failed:", error);
      throw error;
    }
  }

  // -----------------------------
  // Status / Debug
  // -----------------------------
  isReady() {
    return this.initialized && this.stripe && (this.functions || this.modernFunctions);
  }

  isUserAuthenticated() {
    try {
      const u = window.firebase?.auth()?.currentUser;
      return !!(u && !u.isAnonymous && u.email);
    } catch {
      return false;
    }
  }

  getStatus() {
    return {
      initialized: this.initialized,
      stripeReady: !!this.stripe,
      firebaseMode: this.useModernFirebase ? "modern" : "compat",
      functionsReady: !!(this.functions || this.modernFunctions),
      liveMode: this.isLiveMode(),
      authenticated: this.isUserAuthenticated(),
    };
  }
}

// Singleton
const stripeService = new StripeService();

// Expose for debugging
if (typeof window !== "undefined") {
  window.stripeService = stripeService;
  window.stripeServiceStatus = () => stripeService.getStatus();
}

export default stripeService;
